import type { DocNode } from '@atlaskit/adf-schema';
import { adfEncoder } from '@atlassian/conversation-assistant-content-transformer/adf-encoder';
import { luminaToAdf } from '@atlassian/lumina/adf-converter';

import { isAdfDoc } from './isAdfDoc';
import { segmentLuminaAndProse } from './segmentLuminaAndProse';
import { splicePlaceholders } from './splicePlaceholders';

/**
 * Builds the shared ADF doc used by copy and insert/replace paths.
 */
export const buildAdfDocForRichText = (
	content: string | DocNode,
	markdownData?: Record<string, string | undefined>,
): DocNode | null => {
	try {
		if (isAdfDoc(content)) {
			if (!content.content || content.content.length === 0) {
				return null;
			}
			return content;
		}
		const rawPlain = typeof content === 'string' ? content : String(content);
		const splicedForParse = splicePlaceholders(rawPlain, markdownData);
		const chunks = segmentLuminaAndProse(splicedForParse);
		// Prose chunks use the markdown encoder; Lumina XML chunks use the Lumina converter.
		// The resulting top-level ADF content is merged into one document.
		const mergedContent: DocNode['content'] = [];
		for (const chunk of chunks) {
			if (chunk.kind === 'prose') {
				const proseDoc = adfEncoder.encode(chunk.text);
				if (proseDoc.content) {
					mergedContent.push(...proseDoc.content);
				}
			} else {
				const luminaXml =
					chunk.kind === 'lumina-document'
						? chunk.text
						: `<section layout="one-column">${chunk.text}</section>`;
				const luminaDoc = luminaToAdf(luminaXml);
				if (luminaDoc.content) {
					mergedContent.push(...luminaDoc.content);
				}
			}
		}
		if (mergedContent.length === 0) {
			return null;
		}
		return { version: 1, type: 'doc', content: mergedContent };
	} catch {
		return null;
	}
};
